/**
 * MeetingQuickViewModal - Scrollable modal for viewing meeting details
 *
 * Shows meeting details with scrollable content area
 * - For pending meetings: includes approve/decline buttons
 * - For confirmed/completed meetings: view-only mode
 * Used when clicking any date with meetings in the calendar
 */

import React, { useState, useEffect } from 'react';
import {
  X, Calendar, Clock, MapPin, Utensils, Phone, Mail,
  CheckCircle, XCircle, ChevronRight, ChevronLeft, AlertTriangle,
  ShieldCheck, Users, FileText,
  Building, Briefcase, ChevronDown, ChevronUp, UserPlus, ExternalLink, Plus
} from 'lucide-react';
import ProfilePicture from './ProfilePicture';
import { meetingService } from '../utils/supabaseData';
import { preApprovedRepsService } from '../utils/preApprovedRepsService';
import { logger } from '../utils/logger';

const MeetingQuickViewModal = ({
  meeting,
  meetings = [],  // NEW: Array of meetings for navigation (sorted by time)
  isOpen,
  onClose,
  onApprove,
  onDecline,
  onViewDetails,
  onAddMeetingManually,  // NEW: Callback to add meeting manually
  approvedReps = [],
  currentUser
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [addingToApproved, setAddingToApproved] = useState(false);
  const [addedToApproved, setAddedToApproved] = useState(false);
  // All sections collapsed by default
  const [expandedSections, setExpandedSections] = useState({
    salesRep: false,
    location: false,
    restaurant: false,
    details: false
  });

  // Multi-meeting navigation state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);

  // Determine which meetings array to use (backward compatible)
  const meetingsArray = meetings.length > 0 ? meetings : (meeting ? [meeting] : []);
  const currentMeeting = meetingsArray[currentIndex] || meeting;
  const totalMeetings = meetingsArray.length;
  const hasMultipleMeetings = totalMeetings > 1;

  // Navigation handlers
  const goToNextMeeting = () => {
    if (currentIndex < totalMeetings - 1) {
      setCurrentIndex(currentIndex + 1);
      // Reset expanded sections when switching meetings
      setExpandedSections({ salesRep: false, location: false, restaurant: false, details: false });
    }
  };

  const goToPreviousMeeting = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      // Reset expanded sections when switching meetings
      setExpandedSections({ salesRep: false, location: false, restaurant: false, details: false });
    }
  };

  // Touch/swipe handlers for mobile
  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;

    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX - touchEndX;
    const threshold = 50; // Minimum swipe distance

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Swipe left - go to next
        goToNextMeeting();
      } else {
        // Swipe right - go to previous
        goToPreviousMeeting();
      }
    }

    setTouchStartX(null);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'ArrowRight') goToNextMeeting();
      if (e.key === 'ArrowLeft') goToPreviousMeeting();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, totalMeetings]);

  // Reset index when modal closes/opens
  useEffect(() => {
    if (!isOpen) {
      setCurrentIndex(0);
      setExpandedSections({ salesRep: false, location: false, restaurant: false, details: false });
    }
  }, [isOpen]);

  if (!isOpen || !currentMeeting) return null;

  const isPending = currentMeeting.status === 'pending';
  const isConfirmed = currentMeeting.status === 'confirmed';
  const isCompleted = currentMeeting.status === 'completed';
  const isCancelled = currentMeeting.status === 'cancelled';

  // Check if this rep is on the approved list
  const isRepApproved = approvedReps.some(rep =>
    rep.id === currentMeeting.sales_rep_id ||
    rep.email === currentMeeting.salesRepEmail
  );

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Date not specified';
    const date = new Date(dateString + 'T12:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format time for display
  const formatTime = (timeString) => {
    if (!timeString) return '';
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString;
    }
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleApprove = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      const updateData = {
        status: 'confirmed',
        updated_at: new Date().toISOString(),
        confirmed_at: new Date().toISOString()
      };

      const updatedMeeting = await meetingService.updateMeeting(currentMeeting.id, updateData);

      if (updatedMeeting) {
        logger.debug('Meeting approved successfully via modal');
        if (onApprove) onApprove(currentMeeting);
        onClose();
      } else {
        throw new Error('Update returned no data');
      }
    } catch (err) {
      logger.error('Error approving meeting:', err);
      setError('Failed to approve meeting. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDecline = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      const updateData = {
        status: 'cancelled',
        updated_at: new Date().toISOString(),
        cancelled_at: new Date().toISOString()
      };

      const updatedMeeting = await meetingService.updateMeeting(currentMeeting.id, updateData);

      if (updatedMeeting) {
        logger.debug('Meeting declined successfully via modal');
        if (onDecline) onDecline(currentMeeting);
        onClose();
      } else {
        throw new Error('Update returned no data');
      }
    } catch (err) {
      logger.error('Error declining meeting:', err);
      setError('Failed to decline meeting. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddToApproved = async () => {
    if (!currentUser || isRepApproved || addedToApproved) return;

    setAddingToApproved(true);
    try {
      await preApprovedRepsService.addPreApprovedRep(currentUser.id, {
        salesRepId: currentMeeting.sales_rep_id,
        salesRepName: salesRepName,
        salesRepEmail: salesRepEmail,
        salesRepCompany: salesRepCompany,
        salesRepPhone: salesRepPhone
      });
      setAddedToApproved(true);
      logger.debug('Rep added to pre-approved list');
    } catch (err) {
      logger.error('Error adding rep to approved list:', err);
      setError('Failed to add rep to approved list.');
    } finally {
      setAddingToApproved(false);
    }
  };

  // Extract meeting details
  const meetingDate = currentMeeting.meeting_date || currentMeeting.date;
  const meetingTime = currentMeeting.start_time || currentMeeting.time;
  const endTime = currentMeeting.end_time;

  // Sales rep data can come from different sources:
  // 1. Direct fields on meeting (from MeetingsView transform): salesRepName, salesRepEmail, etc.
  // 2. Nested sales_rep object (from getMeetingById): meeting.sales_rep.first_name, etc.
  // 3. External rep fields (for manually added meetings with unregistered reps)
  const salesRepObj = currentMeeting.sales_rep;
  const isExternalRep = !currentMeeting.sales_rep_id && currentMeeting.external_sales_rep_name;

  // Helper to check if a name is a real name (not a placeholder)
  const isRealName = (name) => name && name !== 'Sales Representative' && name !== 'Healthcare Provider';

  const salesRepName =
    (isRealName(currentMeeting.salesRepName) ? currentMeeting.salesRepName : null) ||
    (isRealName(currentMeeting.salesRep) ? currentMeeting.salesRep : null) ||
    (salesRepObj?.first_name ? `${salesRepObj.first_name} ${salesRepObj.last_name || ''}`.trim() : null) ||
    currentMeeting.external_sales_rep_name ||  // External rep fallback
    (isRealName(currentMeeting.customer) ? currentMeeting.customer : null) ||
    'Sales Representative';
  const salesRepCompany = currentMeeting.salesRepCompany || currentMeeting.sales_rep_company ||
    salesRepObj?.company || currentMeeting.external_sales_rep_company || '';
  const salesRepEmail = currentMeeting.salesRepEmail || currentMeeting.sales_rep_email ||
    salesRepObj?.email || currentMeeting.external_sales_rep_email || '';
  const salesRepPhone = currentMeeting.salesRepPhone || currentMeeting.sales_rep_phone ||
    salesRepObj?.phone || currentMeeting.external_sales_rep_phone || '';
  const salesRepJobTitle = currentMeeting.salesRepJobTitle || currentMeeting.sales_rep_job_title || salesRepObj?.job_title || currentMeeting.job_title || '';
  const restaurant = currentMeeting.restaurant_name || currentMeeting.restaurant || '';
  const restaurantAddress = currentMeeting.restaurant_address || '';
  const restaurantPhone = currentMeeting.restaurant_phone || '';

  // Location details
  const locationName = currentMeeting.location_name || currentMeeting.office_name || '';
  const locationAddress = currentMeeting.location_address || currentMeeting.office_address || '';

  // Meeting details
  const attendeeCount = currentMeeting.attendee_count || currentMeeting.total_attendees || 2;
  const notes = currentMeeting.booking_notes || currentMeeting.notes || '';

  // Get status badge config
  const getStatusConfig = () => {
    switch (currentMeeting.status) {
      case 'pending':
        return { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending Approval', dot: 'bg-yellow-500' };
      case 'confirmed':
        return { bg: 'bg-green-100', text: 'text-green-700', label: 'Confirmed', dot: 'bg-green-500' };
      case 'completed':
        return { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Completed', dot: 'bg-blue-500' };
      case 'cancelled':
        return { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled', dot: 'bg-red-500' };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-700', label: currentMeeting.status, dot: 'bg-gray-500' };
    }
  };

  const statusConfig = getStatusConfig();

  // Header gradient based on status
  const getHeaderGradient = () => {
    if (isPending) return 'from-yellow-50 to-orange-50 border-yellow-200';
    if (isConfirmed) return 'from-green-50 to-emerald-50 border-green-200';
    if (isCompleted) return 'from-blue-50 to-indigo-50 border-blue-200';
    if (isCancelled) return 'from-red-50 to-pink-50 border-red-200';
    return 'from-gray-50 to-slate-50 border-gray-200';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal - positioned above bottom nav with safe area padding */}
      <div
        className="relative bg-white w-full sm:w-[420px] sm:max-w-[90vw] rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[85vh] flex flex-col animate-slide-up"
        style={{ marginBottom: 'calc(60px + env(safe-area-inset-bottom, 0px))' }}
      >
        {/* Header - Fixed */}
        <div className={`bg-gradient-to-r ${getHeaderGradient()} border-b px-4 py-3 flex-shrink-0 rounded-t-2xl`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 ${statusConfig.dot} rounded-full ${isPending ? 'animate-pulse' : ''}`} />
              <h3 className="text-lg font-semibold text-gray-800">
                {isPending ? 'Meeting Request' : 'Meeting Details'}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/50 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Navigation controls - only show if multiple meetings */}
        {hasMultipleMeetings && (
          <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b border-gray-200">
            {/* Previous button */}
            <button
              onClick={goToPreviousMeeting}
              disabled={currentIndex === 0}
              className={`p-2 rounded-lg transition-colors ${
                currentIndex === 0
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-200 active:bg-gray-300'
              }`}
              aria-label="Previous meeting"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Dot indicators */}
            <div className="flex items-center space-x-1.5">
              {meetingsArray.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentIndex(index);
                    setExpandedSections({ salesRep: false, location: false, restaurant: false, details: false });
                  }}
                  className={`transition-all rounded-full ${
                    index === currentIndex
                      ? 'bg-blue-600 w-3 h-2'
                      : 'bg-gray-300 hover:bg-gray-400 w-2 h-2'
                  }`}
                  aria-label={`Go to meeting ${index + 1}`}
                />
              ))}
              <span className="ml-2 text-xs text-gray-500">
                {currentIndex + 1} of {totalMeetings}
              </span>
            </div>

            {/* Next button */}
            <button
              onClick={goToNextMeeting}
              disabled={currentIndex === totalMeetings - 1}
              className={`p-2 rounded-lg transition-colors ${
                currentIndex === totalMeetings - 1
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-200 active:bg-gray-300'
              }`}
              aria-label="Next meeting"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Scrollable Content */}
        <div
          className="flex-1 overflow-y-auto overscroll-contain"
          onTouchStart={hasMultipleMeetings ? handleTouchStart : undefined}
          onTouchEnd={hasMultipleMeetings ? handleTouchEnd : undefined}
        >
          <div className="p-4 space-y-3">
            {/* Status Badge */}
            <div className="flex items-center justify-center">
              <span className={`inline-flex items-center px-3 py-1 ${statusConfig.bg} ${statusConfig.text} text-sm font-medium rounded-full`}>
                <div className={`w-2 h-2 ${statusConfig.dot} rounded-full mr-2`} />
                {statusConfig.label}
              </span>
            </div>

            {/* Date and Time - Always visible */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">{formatDate(meetingDate)}</p>
                  <p className="text-sm text-gray-600 flex items-center mt-0.5">
                    <Clock className="w-4 h-4 mr-1.5" />
                    {formatTime(meetingTime)}
                    {endTime && ` - ${formatTime(endTime)}`}
                  </p>
                </div>
              </div>
            </div>

            {/* Sales Rep Section - Collapsible */}
            <div className="bg-gray-50 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection('salesRep')}
                className="w-full flex items-center justify-between p-4 text-left"
              >
                <div className="flex items-center space-x-3">
                  <ProfilePicture
                    src={currentMeeting.sales_rep_profile_picture}
                    name={salesRepName}
                    size="md"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <p className="font-semibold text-gray-900 truncate">{salesRepName}</p>
                      {(isRepApproved || addedToApproved) && (
                        <ShieldCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
                      )}
                    </div>
                    {salesRepCompany && (
                      <p className="text-sm text-gray-500 truncate">{salesRepCompany}</p>
                    )}
                  </div>
                </div>
                {expandedSections.salesRep ? (
                  <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                )}
              </button>

              {expandedSections.salesRep && (
                <div className="px-4 pb-4 space-y-3 border-t border-gray-200 pt-3">
                  {/* Job Title */}
                  {salesRepJobTitle && (
                    <div className="flex items-center space-x-2 text-sm">
                      <Briefcase className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-700">{salesRepJobTitle}</span>
                    </div>
                  )}

                  {/* Phone - Clickable */}
                  {salesRepPhone && (
                    <a
                      href={`tel:${salesRepPhone.replace(/\D/g, '')}`}
                      className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 active:bg-blue-50 -mx-2 px-2 py-1.5 rounded-lg"
                    >
                      <Phone className="w-4 h-4" />
                      <span>{salesRepPhone}</span>
                      <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                    </a>
                  )}

                  {/* Email - Clickable */}
                  {salesRepEmail && (
                    <a
                      href={`mailto:${salesRepEmail}`}
                      className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 active:bg-blue-50 -mx-2 px-2 py-1.5 rounded-lg"
                    >
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{salesRepEmail}</span>
                      <ExternalLink className="w-3 h-3 ml-auto opacity-50 flex-shrink-0" />
                    </a>
                  )}

                  {/* Add to Pre-Approved Button */}
                  {!isRepApproved && !addedToApproved && currentUser && (
                    <button
                      onClick={handleAddToApproved}
                      disabled={addingToApproved}
                      className="w-full flex items-center justify-center space-x-2 mt-2 px-3 py-2.5 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm font-medium hover:bg-green-100 transition-colors disabled:opacity-50"
                    >
                      <UserPlus className="w-4 h-4" />
                      <span>{addingToApproved ? 'Adding...' : 'Add to Pre-Approved Reps'}</span>
                    </button>
                  )}

                  {/* Already Approved Badge */}
                  {(isRepApproved || addedToApproved) && (
                    <div className="flex items-center justify-center space-x-2 mt-2 px-3 py-2 bg-green-50 border border-green-200 rounded-xl">
                      <ShieldCheck className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-700">
                        {addedToApproved ? 'Added to Pre-Approved Reps' : 'Pre-Approved Rep'}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Practice Location Section - Collapsible */}
            {locationName && (
              <div className="bg-purple-50 rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleSection('location')}
                  className="w-full flex items-center justify-between p-4 text-left"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <Building className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{locationName}</p>
                      <p className="text-sm text-gray-500">Practice Location</p>
                    </div>
                  </div>
                  {expandedSections.location ? (
                    <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>

                {expandedSections.location && (
                  <div className="px-4 pb-4 space-y-3 border-t border-purple-200 pt-3">
                    {/* Location Address - Clickable */}
                    {locationAddress && (
                      <a
                        href={`https://maps.apple.com/?q=${encodeURIComponent(locationAddress)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start space-x-2 text-sm text-blue-600 hover:text-blue-800 active:bg-purple-100 -mx-2 px-2 py-1.5 rounded-lg"
                      >
                        <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{locationAddress}</span>
                        <ExternalLink className="w-3 h-3 ml-auto opacity-50 flex-shrink-0 mt-0.5" />
                      </a>
                    )}

                    {!locationAddress && (
                      <p className="text-sm text-gray-400 italic">No address available</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Restaurant Section - Collapsible */}
            {restaurant && (
              <div className="bg-orange-50 rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleSection('restaurant')}
                  className="w-full flex items-center justify-between p-4 text-left"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <Utensils className="w-5 h-5 text-orange-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{restaurant}</p>
                      <p className="text-sm text-gray-500">Restaurant</p>
                    </div>
                  </div>
                  {expandedSections.restaurant ? (
                    <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>

                {expandedSections.restaurant && (
                  <div className="px-4 pb-4 space-y-3 border-t border-orange-200 pt-3">
                    {/* Restaurant Address - Clickable */}
                    {restaurantAddress && (
                      <a
                        href={`https://maps.apple.com/?q=${encodeURIComponent(restaurantAddress)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start space-x-2 text-sm text-blue-600 hover:text-blue-800 active:bg-orange-100 -mx-2 px-2 py-1.5 rounded-lg"
                      >
                        <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span>{restaurantAddress}</span>
                        <ExternalLink className="w-3 h-3 ml-auto opacity-50 flex-shrink-0 mt-0.5" />
                      </a>
                    )}

                    {/* Restaurant Phone - Clickable */}
                    {restaurantPhone && (
                      <a
                        href={`tel:${restaurantPhone.replace(/\D/g, '')}`}
                        className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 active:bg-orange-100 -mx-2 px-2 py-1.5 rounded-lg"
                      >
                        <Phone className="w-4 h-4" />
                        <span>{restaurantPhone}</span>
                        <ExternalLink className="w-3 h-3 ml-auto opacity-50" />
                      </a>
                    )}

                    {!restaurantAddress && !restaurantPhone && (
                      <p className="text-sm text-gray-400 italic">No additional details available</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Meeting Details Section - Collapsible (Attendees & Notes) */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection('details')}
                className="w-full flex items-center justify-between p-4 text-left"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <FileText className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Meeting Details</p>
                    <p className="text-sm text-gray-500">
                      {attendeeCount} attendee{attendeeCount !== 1 ? 's' : ''}
                      {notes ? ' • Has notes' : ''}
                    </p>
                  </div>
                </div>
                {expandedSections.details ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>

              {expandedSections.details && (
                <div className="px-4 pb-4 space-y-3 border-t border-gray-200 pt-3">
                  {/* Attendees */}
                  <div className="flex items-center space-x-2 text-sm">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Attendees:</span>
                    <span className="font-medium text-gray-900">{attendeeCount}</span>
                  </div>

                  {/* Notes */}
                  {notes && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-2">
                      <p className="text-xs text-yellow-700 font-medium mb-1">Notes from Rep</p>
                      <p className="text-sm text-gray-700">{notes}</p>
                    </div>
                  )}

                  {/* No notes message */}
                  {!notes && (
                    <p className="text-sm text-gray-400 italic">No additional notes</p>
                  )}
                </div>
              )}
            </div>

            {/* Cancellation Info */}
            {isCancelled && currentMeeting.cancellation_reason && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <p className="text-xs text-red-700 font-medium mb-1">Cancellation Reason</p>
                <p className="text-sm text-gray-700">{currentMeeting.cancellation_reason}</p>
                {currentMeeting.cancelled_by_name && (
                  <p className="text-xs text-gray-500 mt-1">
                    Cancelled by: {currentMeeting.cancelled_by_name}
                  </p>
                )}
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>
        </div>

        {/* Actions - Fixed at bottom */}
        <div className="border-t border-gray-200 p-4 bg-gray-50 flex-shrink-0 rounded-b-2xl sm:rounded-b-2xl">
          {/* Approve/Decline for pending meetings */}
          {isPending && (
            <div className="flex space-x-3 mb-3">
              <button
                onClick={handleDecline}
                disabled={isProcessing}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-white border border-red-200 text-red-600 rounded-xl font-medium hover:bg-red-50 transition-colors disabled:opacity-50"
              >
                <XCircle className="w-5 h-5" />
                <span>Decline</span>
              </button>
              <button
                onClick={handleApprove}
                disabled={isProcessing}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                <CheckCircle className="w-5 h-5" />
                <span>Approve</span>
              </button>
            </div>
          )}

          {/* Action buttons row */}
          <div className="flex space-x-3">
            {/* View Details Link */}
            <button
              onClick={() => {
                if (onViewDetails) onViewDetails(currentMeeting);
                onClose();
              }}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 text-blue-600 hover:bg-blue-50 rounded-xl transition-colors border border-blue-200"
            >
              <span className="text-sm font-medium">View Details</span>
              <ChevronRight className="w-4 h-4" />
            </button>

            {/* Add Meeting Button - only show for customers/office_staff */}
            {onAddMeetingManually && (
              <button
                onClick={() => {
                  const meetingDate = currentMeeting.meeting_date || currentMeeting.date;
                  onAddMeetingManually(meetingDate);
                  onClose();
                }}
                className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-green-600 text-white hover:bg-green-700 rounded-xl transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm font-medium">Add Meeting</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default MeetingQuickViewModal;
